import { usePage } from "@inertiajs/react"
import { PublicHero, PublicShell } from "@/components/PublicShell"
import { PublicMeta } from "@/components/public/PublicMeta"
import { AccountPromptCard } from "@/components/public/AccountPromptCard"
import { EstimateLine, bottles } from "@/components/public/SelectionTray"
import type { PageProps } from "@/types/inertia"
import "@/styles/public-order.css"

const TITLE = "Thank You for Your Order Enquiry"
const DESCRIPTION =
  "Your wine selection has reached SOH GD Wines. We will be in touch personally to confirm the wines, your label, delivery and the final quote."

type SummaryLine = {
  wine_id: number
  name: string
  producer: string
  wine_type_label: string
  bottle_count: number
}

type ThankYouProps = {
  // From the one-time flash set by OrdersController#create; null on a revisit.
  summary: {
    name: string
    bottle_count: number
    estimated_total_pence: number | null
    lines: SummaryLine[]
    // Label mock-ups attached to the enquiry (M4).
    mockup_count: number
    // The new enquiry, for the signed-in "Track it from your account" link (M6).
    enquiry_id: number | null
  } | null
}

export default function OrderThankYou() {
  const { props } = usePage<PageProps<ThankYouProps>>()
  const { summary } = props

  return (
    <PublicShell>
      <PublicMeta title={TITLE} description={DESCRIPTION} />

      <PublicHero
        eyebrow="Order enquiry received"
        title={
          <>
            Thank you{summary?.name ? `, ${summary.name}` : ""}.{" "}
            <span className="soft">Your selection is with us.</span>
          </>
        }
        lede="A copy is on its way to your inbox. We will contact you personally to confirm the wines, your label and delivery, and to agree the final quote."
      />

      <section className="band band-charcoal pt-0" aria-label="Your order enquiry">
        <div className="shell">
          <div className="grid gap-16 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] lg:gap-20">
            <div className="min-w-0">
              {summary && summary.lines.length > 0 ? (
                <>
                  <div className="eyebrow">What you sent us</div>
                  <h2 className="mb-8">Your selection</h2>
                  <ul className="ord-thanks-list">
                    {summary.lines.map((line) => (
                      <li key={line.wine_id}>
                        <span className="min-w-0">
                          <span className="ord-wine-name">{line.name}</span>
                          <span className="ord-wine-meta">
                            {line.producer} · {line.wine_type_label}
                          </span>
                        </span>
                        <span className="ord-price whitespace-nowrap">{bottles(line.bottle_count)}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="ord-thanks-total">
                    <span className="ord-overline">Total</span>
                    <span className="ord-summary-count">{bottles(summary.bottle_count)}</span>
                  </div>
                  <EstimateLine pence={summary.estimated_total_pence} className="mt-4 text-right" />
                  {summary.mockup_count > 0 && (
                    <p className="ord-submit-note mt-8">
                      {summary.mockup_count === 1
                        ? "Your label design is attached to your enquiry."
                        : `Your ${summary.mockup_count} label designs are attached to your enquiry.`}{" "}
                      We will look at {summary.mockup_count === 1 ? "it" : "them"} with you at consultation.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <div className="eyebrow">What happens next</div>
                  <h2>We will be in touch</h2>
                  <p className="mt-4 max-w-[52ch]">
                    Your enquiry and the full list of wines are in the confirmation email we sent
                    you. Reply to it if anything has changed.
                  </p>
                </>
              )}
            </div>

            <AccountPromptCard
              noun="order"
              titleId="order-account-title"
              trackHref={summary?.enquiry_id ? `/account/orders/${summary.enquiry_id}` : "/account/orders"}
            />
          </div>
        </div>
      </section>
    </PublicShell>
  )
}
